/**
 * PortfolioBand — P4's desktop portfolio workspace (CD-1).
 *
 * Renders at `lg+` only, full-rail, ABOVE the masonry columns. Its job is the
 * property manager's actual morning question — "which of my properties needs me
 * today?" — answered in one glance rather than four drill-ins: every managed
 * account as a comparison row (property · balance · usage · status), with the
 * portfolio's remaining balance and the batch-pay CTA in the band header.
 *
 * Why it is a component and not a wider `PortfolioSummaryCard`: below `lg` the
 * card is still the right shape (one column, stacked), so the two coexist —
 * `heroBandCards()` decides which one the viewport gets, and the card keeps
 * rendering unchanged for every width under `lg`.
 *
 * Web-only by design: commercial personas are desktop-by-design per the locked
 * boundary (`project_mobile_commercial_desktop_only`), so there is no mobile
 * sibling to share with. The DOMAIN truth is still single-sourced — every figure
 * here comes from the shared `useBillState` resolver via props, never from a
 * second local computation.
 *
 * Atlas: B1.
 */
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import type { ManagedAccountSummary } from "@petci/fixtures";
import type { BillState } from "@petci/shared-ui";
import { formatCurrency, formatDate, formatKwh } from "@/lib/format";
import { useT, useLocaleTag } from "@/lib/locale";
import { VacantModeBadge } from "@/components/VacantModeBadge";

interface Props {
  accounts: ManagedAccountSummary[];
  /** The shared resolver's answer for this persona — the ONLY source of money. */
  state: BillState;
}

export function PortfolioBand({ accounts, state }: Props) {
  const t = useT();
  const tag = useLocaleTag();
  const dueIso = accounts[0]?.currentBillDueDate;

  return (
    <section
      aria-labelledby="portfolio-band-title"
      className="hidden lg:block card-dark rounded-2xl p-6 space-y-5"
    >
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <h2
            id="portfolio-band-title"
            className="text-white/70 text-xs font-medium uppercase tracking-wider"
          >
            {t("home.portfolioBalance")}
          </h2>
          {/* The headline is what the portfolio still OWES, so a batch payment
              visibly settles it. The per-property rows below keep their GROSS
              bill amounts: payments are recorded against the portfolio, not
              attributed to individual accounts, so zeroing a row would claim an
              attribution the ledger does not carry. */}
          <p className="text-4xl font-display font-bold mt-1 tabular-nums">
            {formatCurrency(state.remaining)}
          </p>
          <p className="text-white/70 text-sm mt-1">
            {state.isSettled
              ? t("home.accountsCountPaid", {
                  count: accounts.length,
                  date: formatDate(state.settledOnIso, tag),
                })
              : t("home.accountsCountDue", {
                  count: accounts.length,
                  date: formatDate(dueIso, tag),
                })}
          </p>
        </div>
        {/* `btn-primary-on-dark` rather than the card's hand-rolled white
            button: the primitive carries a `focus-visible` ring, which the
            inline version has never had — on a dark surface a keyboard user
            otherwise has no visible focus at all. The <lg card keeps its own
            styling so its render stays byte-identical to before this brief. */}
        <Link href="/batch-pay" className="btn-primary-on-dark flex-shrink-0">
          {t("pay.batchPayAll")}
        </Link>
      </div>

      <table className="w-full text-sm">
        <caption className="sr-only">{t("pm.managedAccountsAria")}</caption>
        <thead>
          <tr className="text-white/70 text-xs uppercase tracking-wider">
            <th scope="col" className="text-left font-medium pb-2 pl-3">
              {t("pm.property")}
            </th>
            <th scope="col" className="text-right font-medium pb-2 px-3">
              {t("pm.thBalance")}
            </th>
            <th scope="col" className="text-right font-medium pb-2 px-3">
              {t("pm.thUsage")}
            </th>
            <th scope="col" className="text-left font-medium pb-2 pl-3">
              {t("pm.thStatus")}
            </th>
          </tr>
        </thead>
        <tbody>
          {accounts.map((a) => (
            <tr
              key={a.accountNumber}
              className="border-t border-white/10 hover:bg-white/5 transition-colors duration-fast"
            >
              {/* The MeterCard left-accent idiom, in the dark-surface palette:
                  the amber `-text` warning step is tuned for light cards and
                  drops to ~1.6:1 here, so the accent is brand gold — the same
                  substitution VacantModeBadge documents. */}
              <td
                className={[
                  "py-2.5 pl-3 border-l-2",
                  a.anomalyBadge ? "border-l-brand-accent" : "border-l-transparent",
                ].join(" ")}
              >
                <Link
                  href={`/pm/account/${a.accountNumber}`}
                  aria-label={t("pm.viewPropertyAria", { name: a.propertyName })}
                  className="group inline-flex items-center gap-1.5 font-medium break-words hover:underline"
                >
                  {a.propertyName}
                  <ChevronRight
                    size={14}
                    aria-hidden
                    className="flex-shrink-0 text-white/70 group-hover:translate-x-0.5 transition-transform duration-fast"
                  />
                </Link>
              </td>
              <td className="py-2.5 px-3 text-right font-mono tabular-nums">
                {formatCurrency(a.currentBillAmount)}
              </td>
              <td className="py-2.5 px-3 text-right tabular-nums text-white/70">
                {formatKwh(a.usageKwh)}
              </td>
              <td className="py-2.5 pl-3">
                <span className="inline-flex items-center gap-2 flex-wrap">
                  {a.anomalyBadge && <VacantModeBadge label={t("home.anomaly")} />}
                  {a.isVacant && <span className="text-white/70">{t("pm.vacant")}</span>}
                  {!a.anomalyBadge && !a.isVacant && (
                    <span className="text-white/70">{t("pm.statusNormal")}</span>
                  )}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
